import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  signIn as firebaseSignIn,
  signUp as firebaseSignUp,
  signOut as firebaseSignOut,
  signInWithGoogle as firebaseSignInWithGoogle,
  onAuthStateChanged,
} from '@diggens/api-client';
import type { FirebaseUser } from '@diggens/api-client';

// NOTE: useCurrentUser has been moved to '@diggens/user-management'
// Import it from there instead: import { useCurrentUser } from '@diggens/user-management';

// Custom hook for session management (Firebase auth state)
export function useSession() {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged((firebaseUser) => {
      setUser(firebaseUser);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return { data: user, isLoading };
}

// Sign in hook - returns async function
export function useSignIn() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async ({ email, password }: { email: string; password: string }) => {
    setIsPending(true);
    setError(null);
    try {
      const result = await firebaseSignIn(email, password);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Sign in failed');
      setError(error);
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending, error };
}

// Sign up hook
export function useSignUp() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async (params: { email: string; password: string; firstName: string; lastName: string; organization: string }) => {
    setIsPending(true);
    setError(null);
    try {
      const result = await firebaseSignUp(params.email, params.password);

      // Register user profile + tenant on the backend
      const { apiFetch } = await import('@diggens/api-client');
      await apiFetch('/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          firstName: params.firstName,
          lastName: params.lastName,
          organization: params.organization,
        }),
      });

      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Sign up failed');
      setError(error);
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending, error };
}

// Sign out hook
export function useSignOut() {
  const queryClient = useQueryClient();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async () => {
    setIsPending(true);
    setError(null);
    try {
      await firebaseSignOut();
      // Drop every cached query and mutation, so whoever signs in next on this tab never sees this user's data
      queryClient.clear();
      // Clear persisted auth store so UI doesn't show stale state
      // The store key matches useAuthStore's persist config ('diggens-auth-storage')
      try { localStorage.removeItem('diggens-auth-storage'); } catch { /* SSR-safe */ }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Sign out failed');
      setError(error);
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending, error };
}

// Sign in with Google hook
export function useSignInWithGoogle() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async () => {
    setIsPending(true);
    setError(null);
    try {
      const result = await firebaseSignInWithGoogle();
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Google sign in failed');
      setError(error);
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending, error };
}
